import { documentTitleFromProposal } from "@albertine/design-system/utils/shareDocumentTitle";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Lib } from "albertine-shared-web";
import type { PublicBooking, PublicOption } from "albertine-shared-web/types";
import { useState } from "react";
import { BackButton } from "@/routes/-components/BackButton";
import { BookingContent } from "@/routes/-components/booking/BookingContent";
import { BookingHero } from "@/routes/-components/booking/BookingHero";
import {
    BOOKING_FOOTER_BACKGROUND,
    BOOKING_HERO_BACKGROUND,
    BOOKING_REVEAL_BACKGROUND_CLASS,
    BookingPageLayout,
} from "@/routes/-components/booking/BookingPageLayout";
import { CallToAction } from "@/routes/-components/CallToAction";
import { useShareLink } from "@/routes/-hooks/useShareLink";
import { useSkeletonRevealPage } from "@/routes/-hooks/useSkeletonRevealPage";
import { OptionPickerSheet } from "./-components/OptionPickerSheet";
import { SharedProposalPage } from "./-components/SharedProposalPage";
import {
    ProposalProvider,
    useIsProposalLoaded,
    useProposal,
} from "./-context/ProposalContext";
import { SharedProposalProvider } from "./-context/SharedProposalContext";
import { useProposalActions } from "./-hooks/useProposalActions";
import { conciergeProposalToPublicProposal } from "./-utils/proposalDataMapper";
import {
    isAgentPreviewSearch,
    isPublicShareSearch,
    proposalSearchSchema,
} from "./-utils/proposalSearchSchema";

export const Route = createFileRoute("/proposal/")({
    validateSearch: proposalSearchSchema,
    component: RouteComponent,
    head: () => ({ meta: [{ title: "Proposal" }] }),

    beforeLoad: ({ search }) => {
        const isSkeletonRevealed = !(
            isPublicShareSearch(search) || isAgentPreviewSearch(search)
        );
        document.documentElement.style.setProperty(
            "--color-body-top",
            BOOKING_HERO_BACKGROUND,
        );
        document.documentElement.style.setProperty(
            "--color-body-bottom",
            isSkeletonRevealed ? BOOKING_HERO_BACKGROUND : BOOKING_FOOTER_BACKGROUND,
        );
    },

    onError: () => {
        throw notFound();
    },
});

function RouteComponent() {
    const search = Route.useSearch();

    if (isPublicShareSearch(search) || isAgentPreviewSearch(search)) {
        return (
            <SharedProposalProvider>
                <SharedProposalPage />
            </SharedProposalProvider>
        );
    }

    return (
        <Lib.Context.BottomSheetProvider>
            <ProposalProvider>
                <ProposalPage />
            </ProposalProvider>
        </Lib.Context.BottomSheetProvider>
    );
}

function ProposalPage() {
    const proposal = useProposal();
    const isLoaded = useIsProposalLoaded();
    const reveal = useSkeletonRevealPage({
        isLoaded,
        loadingBodyColor: BOOKING_HERO_BACKGROUND,
        revealedBodyColor: BOOKING_FOOTER_BACKGROUND,
    });
    const { tenant, locale } = Route.useSearch();
    const { openBottomSheet } = Lib.Context.useBottomSheetContext();
    const { selectProposalOption, openChat } = useProposalActions(
        proposal,
        tenant,
        locale,
    );
    const { share, isPending: isSharePending } = useShareLink({
        resourceType: "proposal",
        resourceId: proposal.request.id,
        title: proposal.request.title ?? "",
    });
    const publicProposal = conciergeProposalToPublicProposal(proposal);

    const [selectedBooking, setSelectedBooking] = useState<PublicBooking | undefined>();
    const [selectedOption, setSelectedOption] = useState<PublicOption | undefined>();

    function handleBook(booking: PublicBooking & { requestId: string }) {
        const options = booking.options ?? [];

        if (options.length >= 2) {
            setSelectedBooking(booking);
            setSelectedOption(undefined);
            openBottomSheet(true);
            return;
        }

        selectProposalOption({ bookingId: booking.id, optionId: options[0]?.id });
    }

    function handleContinue() {
        if (!selectedBooking) {
            return;
        }

        selectProposalOption({
            bookingId: selectedBooking.id,
            optionId: selectedOption?.id,
        });
        openBottomSheet(false);
    }

    return (
        <>
            <BookingPageLayout
                id={publicProposal.id}
                tenant={tenant}
                title={publicProposal.title}
                documentTitle={documentTitleFromProposal(publicProposal)}
                hero={<BookingHero.Proposal proposal={publicProposal} />}
                backButton={<BackButton />}
                shareAction={{ onShare: share, isPending: isSharePending }}
                reveal={reveal}
                footer={
                    <CallToAction
                        openChat={openChat}
                        className={BOOKING_REVEAL_BACKGROUND_CLASS}
                    />
                }
            >
                {publicProposal.bookings.map((booking, index) => (
                    <BookingContent.Proposal
                        key={booking.id}
                        booking={booking}
                        index={index}
                        requestId={publicProposal.id}
                        isLoaded={isLoaded}
                        onBookClick={
                            reveal.isVisibleContentRevealed ? handleBook : undefined
                        }
                    />
                ))}
            </BookingPageLayout>

            <OptionPickerSheet
                booking={selectedBooking}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                onContinue={handleContinue}
            />
        </>
    );
}
